import React, {useMemo, useState} from 'react';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import {MuiTelInput} from 'mui-tel-input';
import {INDUSTRIES} from '../../constants/industries';
import {COUNTRIES} from '../../constants/countries';
import {NewUser} from './types';
import Alert from '@mui/material/Alert';
import LoadingButton from '@mui/lab/LoadingButton';
import {EMAIL_REGEX} from "../../constants/regex";

const initialFormData = {
    companyName: '',
    country: {id: '', label: ''},
    email: ``,
    industry: {id: '', label: ''},
    nationalPhone: '',
    name: '',
    phone: ``,
}

type RegisterFormProps = {
    errorMessage?: string,
    isLoading: boolean,
    onSubmit: (newUser: NewUser) => void,
}

export const RegisterForm = (props: RegisterFormProps) => {
    const {errorMessage, isLoading, onSubmit} = props

    const [formData, setFormData] = useState(initialFormData)
    const [isEmailValid, setIsEmailValid] = useState(true)

    const industries = useMemo(() => INDUSTRIES.map(industry => ({
        label: industry,
        id: industry,
    })), [])

    const countries = useMemo(() => COUNTRIES.map(country => ({
        label: country,
        id: country,
    })), [])

    const updateFormData = (newValues: Partial<typeof initialFormData>) => {
        if (newValues.email !== undefined) {
            if (EMAIL_REGEX.test(newValues.email)) {
                setIsEmailValid(true)
            } else {
                setIsEmailValid(false)
            }
        }

        setFormData(oldState => ({
            ...oldState,
            ...newValues,
        }))
    }

    return (
        <Stack gap="1.5rem">
            <TextField
                disabled={isLoading}
                label="Full Name"
                onChange={(e) => updateFormData({name: e.target.value})}
                value={formData.name}
                variant="outlined"
            />

            <TextField
                disabled={isLoading}
                label="Company Name"
                variant="outlined"
                value={formData.companyName}
                onChange={(e) => updateFormData({companyName: e.target.value})}
            />

            <Autocomplete
                disableClearable
                disablePortal
                disabled={isLoading}
                onChange={(e, newValue) => updateFormData({country: newValue})}
                options={countries}
                renderInput={(params) => <TextField {...params} label="Country" />}
                value={formData.country}
            />

            <Autocomplete
                disableClearable
                disablePortal
                disabled={isLoading}
                onChange={(e, newValue) => updateFormData({industry: newValue})}
                options={industries}
                renderInput={(params) => <TextField {...params} label="Industry" />}
                value={formData.industry}
            />

            <TextField
                disabled={isLoading}
                error={!isEmailValid}
                helperText={!isEmailValid && 'Please enter valid email'}
                label="Email"
                onChange={(e) => updateFormData({email: e.target.value})}
                value={formData.email}
                variant="outlined"
            />

            <MuiTelInput
                defaultCountry="US"
                disabled={isLoading}
                focusOnSelectCountry
                forceCallingCode
                onChange={(value, info) => updateFormData({
                    phone: value,
                    nationalPhone: info.nationalNumber ?? '',
                })}
                value={formData.phone}
            />

            {errorMessage ? (
                <Alert severity="error">{errorMessage}</Alert>
            ): null}

            <LoadingButton
                disabled={
                    !isEmailValid
                    || formData.companyName === ""
                    || formData.country.id === ""
                    || formData.email === ""
                    || formData.industry.id === ""
                    || formData.nationalPhone === ""
                    || formData.name === ""
                }
                loading={isLoading}
                size="large"
                variant="contained"
                onClick={() => onSubmit({
                    company: formData.companyName,
                    country: formData.country.label,
                    email: formData.email,
                    industry: formData.industry.label,
                    name: formData.name,
                    phone: formData.phone,
                })}
            >
                Register
            </LoadingButton>
        </Stack>
    )
}
